import { memo } from "react";
import Radio from "~/common/components/Radio";
import { Controller, type UseFormReturn } from "react-hook-form";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";

import classes from "~/features/orders/orderList/components/OrderListSearchBox/SearchOrderStatusLineSelector/SearchOrderStatusLineSelector.module.less";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";

interface SearchPeriodSelectorProps {
  statusDropdown: DropdownEntry<string>[];
  lineStatusDropdown: DropdownEntry<string>[];
  formHooks: UseFormReturn<any>;
  variant: SearchBoxVariant;
  disabled?: boolean;
}

const VARIANT_MAP: Dict<{ documentLabelKey: string; lineItemLabelKey: string }> = {
  [SearchBoxVariant.ORDER]: {
    documentLabelKey: "orderList.orderStatus",
    lineItemLabelKey: "orderList.orderLineStatus",
  },
  [SearchBoxVariant.QUOTE]: {
    documentLabelKey: "quoteList.quoteStatus",
    lineItemLabelKey: "quoteList.quoteLineStatus",
  },
};

const SearchOrderStatusLineSelector = ({
  statusDropdown,
  lineStatusDropdown,
  formHooks,
  variant,
  disabled,
}: SearchPeriodSelectorProps) => {
  const { t } = useTranslation();
  const propsForm = {
    shouldValidate: true,
    shouldDirty: true,
  };

  return (
    <div className={classes.container}>
      {statusDropdown && (
        <div className={classes.optionContainer}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Radio
                active={value}
                disabled={disabled}
                onClick={() => {
                  onChange(true);
                  formHooks.setValue("isLineStatus", false, propsForm);
                }}
              />
            )}
            name={"isStatus"}
            control={formHooks.control}
            defaultValue={true}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <SimpleSelect
                label={t(VARIANT_MAP[variant].documentLabelKey)}
                values={statusDropdown}
                value={value}
                fullWidth={true}
                displayEmpty={true}
                shrink={true}
                disabled={disabled}
                onChange={(e) => {
                  formHooks.setValue("isStatus", true, propsForm);
                  onChange(e);
                  formHooks.setValue("isLineStatus", false, propsForm);
                }}
                data-test-id={"search-order-quote-status"}
              />
            )}
            name={"statusOption"}
            control={formHooks.control}
            defaultValue={statusDropdown[0] ? statusDropdown[0].value : ""}
          />
        </div>
      )}
      {statusDropdown && lineStatusDropdown && (
        <div className={classes.or}>{t("searchPage.refinement.value.label.or")}</div>
      )}
      {lineStatusDropdown && (
        <div className={classes.optionContainer}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Radio
                active={value}
                disabled={disabled}
                onClick={() => {
                  onChange(true);
                  formHooks.setValue("isStatus", false, propsForm);
                }}
              />
            )}
            name={"isLineStatus"}
            control={formHooks.control}
            defaultValue={false}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <SimpleSelect
                label={t(VARIANT_MAP[variant].lineItemLabelKey)}
                values={lineStatusDropdown}
                value={value}
                fullWidth={true}
                displayEmpty={true}
                shrink={true}
                disabled={disabled}
                onChange={(e) => {
                  formHooks.setValue("isLineStatus", true, propsForm);
                  onChange(e);
                  formHooks.setValue("isStatus", false, propsForm);
                }}
                data-test-id={"search-order-quote-line-status"}
              />
            )}
            name={"lineStatusOption"}
            control={formHooks.control}
            defaultValue={lineStatusDropdown[0] ? lineStatusDropdown[0].value : ""}
          />
        </div>
      )}
    </div>
  );
};

export default memo(SearchOrderStatusLineSelector);
